<script setup lang="ts">
import { computed } from 'vue';
import { VehicleSymbol } from 'shared/vehicles/domain/vehicleSymbol';
import type { VehicleShapeDefinition } from 'shared/map/domain/vehicleSymbols/vehicleShape';
import { arrowShape } from 'shared/map/domain/vehicleSymbols/arrowShape';
import { wheelLoaderFrontGrappleShape } from 'shared/map/domain/vehicleSymbols/wheelLoaderFrontGrappleShape';
import { wheelLoaderHighGrappleShape } from 'shared/map/domain/vehicleSymbols/wheelLoaderHighGrappleShape';
import { timberCraneShape } from 'shared/map/domain/vehicleSymbols/timberCraneShape';
import { coilForkliftShape } from 'shared/map/domain/vehicleSymbols/coilForkliftShape';
import { logStackerShape } from 'shared/map/domain/vehicleSymbols/logStackerShape';

const props = defineProps<{
  symbol: VehicleSymbol;
  color?: string;
  size?: number;
}>();

const vehicleColor = computed(() => props.color ?? '#91a7ff');
const previewSize = computed(() => props.size ?? 120);

function getShapeDefinition(symbol: VehicleSymbol): VehicleShapeDefinition {
  switch (symbol) {
    case VehicleSymbol.LogStacker:
      return logStackerShape;
    case VehicleSymbol.WheelLoaderFrontGrapple:
      return wheelLoaderFrontGrappleShape;
    case VehicleSymbol.WheelLoaderHighGrapple:
      return wheelLoaderHighGrappleShape;
    case VehicleSymbol.TimberCrane:
      return timberCraneShape;
    case VehicleSymbol.CoilForklift:
      return coilForkliftShape;
    case VehicleSymbol.Undefined:
    default:
      return arrowShape;
  }
}

const shapeDefinition = computed(() => getShapeDefinition(props.symbol));

// Convert polygon coordinates to SVG path
function polygonToPath(coords: Array<[number, number]>): string {
  if (coords.length === 0) return '';

  const pathParts = coords.map((coord, index) => {
    const [x, y] = coord;
    const command = index === 0 ? 'M' : 'L';
    return `${command} ${x} ${y}`;
  });

  return pathParts.join(' ') + ' Z';
}

const polygonPaths = computed(() => {
  const colorConfig = shapeDefinition.value.colorConfig || {};

  return Object.entries(shapeDefinition.value.polygons).map(([name, coords]) => {
    // Determine the fill color based on colorConfig
    let fillColor = vehicleColor.value; // Default to dynamic vehicle color

    const config = colorConfig[name];
    if (config) {
      if (config.type === 'fixed' && config.color) {
        fillColor = config.color;
      }
      // If type is 'dynamic', use the default vehicle color (already set)
    }

    return {
      name,
      path: polygonToPath(coords),
      fillColor
    };
  });
});

// ViewBox for the SVG (normalized coordinates are 0-100)
const viewBox = '0 0 100 150';
</script>

<template>
  <div class="vehicle-symbol-preview">
    <svg :width="previewSize" :height="previewSize * 1.5" :viewBox="viewBox" class="vehicle-svg">
      <g v-for="polygon in polygonPaths" :key="polygon.name">
        <path :d="polygon.path" :fill="polygon.fillColor" stroke="#000000" stroke-width="2" opacity="0.9" />
      </g>
    </svg>
  </div>
</template>

<style scoped>
.vehicle-symbol-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.vehicle-svg {
  display: block;
}
</style>
